import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CalendarDays,
  ChevronDown,
  ClipboardList,
  FileCheck2,
  FileClock,
  FileDown,
  LucideAngularModule,
  Search,
  X,
  type LucideIconData,
} from 'lucide-angular';
import { catchError, firstValueFrom, of } from 'rxjs';
import { Charge, Contract, Loa, LoaBillConfig, LoaSchedule } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { LoaService } from '../../loa/services/loa.service';
import { ChargesService } from '../../charges/services/charges.service';
import { ContractsService, type ContractAnalysisReportResponse, type ContractAnalysisTransactionType } from '../services/contracts.service';

type TransactionTypeFilter = 'all' | 'sale_purchase' | 'banking' | 'hybrid';

interface ApplicationChargeApi {
  group_name: string;
  section_heading?: string | null;
  sub_group_name: string;
  rate_mwh?: number | null;
  quantum_mwh?: number | null;
  amount?: number | null;
}

interface ApplicationApi {
  id: string;
  contract_id: string;
  application_number?: string | null;
  seller_loa_id?: string | null;
  buyer_loa_id?: string | null;
  approval_charges?: ApplicationChargeApi[];
}

interface OaChargeAllocation {
  charge_type: string;
  payer: 'Buyer' | 'Seller' | 'Shared';
  buyer_percent: number;
  seller_percent: number;
}

interface EstimatedProfitChargeRow {
  applicationNumber: string;
  chargeType: string;
  rateType?: string | null;
  totalAmount: number;
  buyerAmount: number;
  sellerAmount: number;
  seciAmount: number;
  allocationLabel: string;
}

interface EstimatedProfitSummary {
  revenue: number;
  cost: number;
  applicationCharges: number;
  netProfit: number | null;
  chargeRows: EstimatedProfitChargeRow[];
}

interface ContractAnalysisMetric {
  label: string;
  value: string;
  valueClass: string;
  icon: LucideIconData;
  subValue?: string;
  subValueClass?: string;
}

interface ContractAnalysisRow {
  contractDbId: string;
  contractNumber: string;
  contractId: string;
  buyerLoa: string;
  sellerLoa: string;
  transactionType: Exclude<TransactionTypeFilter, 'all'>;
  buyerName: string;
  sellerName: string;
  totalQuantumMw: number;
  totalQuantumMwh: number;
  approvedQuantumMw: number;
  deliveredMwh: number;
  deviationPercent: number;
  estimatedProfit: number | null;
  actualProfit: number | null;
  daysDifference: number;
  timeDifference: number;
  periodFrom: Date | null;
  periodTo: Date | null;
  periodLabel: string;
}

interface EstimatedProfitModalData {
  title: string;
  subtitle: string;
  equation: string;
  hint: string;
  summaryLine: string;
  details: Array<{ label: string; value: string }>;
}

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './contract-analysis.html',
  styleUrls: ['./contract-analysis.scss'],
})
export class ContractAnalysisPage implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly contractsService = inject(ContractsService);
  private readonly api = inject(ApiService);
  private readonly loaService = inject(LoaService);
  private readonly chargesService = inject(ChargesService);

  protected readonly SearchIcon = Search;
  protected readonly ExportIcon = FileDown;
  protected readonly ChevronDownIcon = ChevronDown;
  protected readonly FileTextIcon = ClipboardList;
  protected readonly CloseIcon = X;

  protected readonly searchTerm = signal('');
  protected readonly transactionType = signal<TransactionTypeFilter>('all');
  protected readonly loading = signal(true);
  protected readonly loadError = signal<string | null>(null);
  protected readonly estimatedProfitModal = signal<EstimatedProfitModalData | null>(null);

  private readonly contracts = signal<Contract[]>([]);
  private readonly loas = signal<Loa[]>([]);
  private readonly rawApplications = signal<ApplicationApi[]>([]);
  private readonly chargeMasters = signal<Charge[]>([]);
  private readonly billConfigsByLoa = signal<Record<string, LoaBillConfig[]>>({});
  private readonly loaSchedulesByLoa = signal<Record<string, LoaSchedule[]>>({});
  private readonly computedEstimatedProfits = signal<Record<string, number | null>>({});
  private readonly computedActualProfits = signal<Record<string, number | null>>({});
  private readonly contractsLoading = signal(false);
  private readonly loasLoading = signal(false);
  private readonly applicationsLoading = signal(false);
  private readonly chargesLoading = signal(false);
  private readonly billConfigsRequests = new Map<string, Promise<void>>();
  private readonly loaScheduleRequests = new Map<string, Promise<void>>();

  private readonly rows = signal<ContractAnalysisRow[]>([]);
  private readonly summary = signal<ContractAnalysisReportResponse['summary']>({
    total_scheduled_mwh: 0,
    total_delivered_mwh: 0,
    applications_punched: 0,
    applications_approved: 0,
    active_contracts: 0,
    approval_rate: 0,
  });

  protected readonly metrics = computed<ContractAnalysisMetric[]>(() => {
    const summary = this.summary();
    const totalScheduled = Number(summary.total_scheduled_mwh || 0);
    const totalDelivered = Number(summary.total_delivered_mwh || 0);
    const applicationsPunched = Number(summary.applications_punched || 0);
    const applicationsApproved = Number(summary.applications_approved || 0);
    const activeContracts = Number(summary.active_contracts || 0);
    const approvalRate = Number.isFinite(Number(summary.approval_rate)) ? Number(summary.approval_rate) : applicationsPunched ? (applicationsApproved / applicationsPunched) * 100 : 0;

    return [
      { label: 'Total Scheduled', value: `${totalScheduled.toLocaleString('en-IN')} MWh`, valueClass: 'value-blue', icon: CalendarDays },
      { label: 'Total Delivered', value: `${totalDelivered.toLocaleString('en-IN')} MWh`, valueClass: 'value-purple', icon: FileCheck2 },
      { label: 'Applications Punched', value: `${applicationsPunched}`, valueClass: 'value-red', icon: FileClock },
      {
        label: 'Applications Approved',
        value: `${applicationsApproved}`,
        valueClass: 'value-green',
        icon: FileCheck2,
        subValue: `${approvalRate.toFixed(1)}% Approval Rate`,
        subValueClass: 'subvalue-green',
      },
      { label: 'Active Contracts', value: `${activeContracts}`, valueClass: 'value-orange', icon: ClipboardList },
    ];
  });

  protected readonly filteredRows = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const txType = this.transactionType();

    return this.rows().filter((row) => {
      const matchesType = txType === 'all' || row.transactionType === txType;
      if (!matchesType) return false;
      if (!term) return true;

      return (
        row.contractId.toLowerCase().includes(term) ||
        row.buyerLoa.toLowerCase().includes(term) ||
        row.sellerLoa.toLowerCase().includes(term) ||
        row.buyerName.toLowerCase().includes(term) ||
        row.sellerName.toLowerCase().includes(term) ||
        row.periodLabel.toLowerCase().includes(term)
      );
    });
  });

  ngOnInit(): void {
    this.fetchReport();
  }

  private fetchReport(): void {
    this.loading.set(true);
    this.loadError.set(null);

    this.contractsService
      .analysisReport()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          const message = (error?.error?.detail || error?.message || 'Failed to load report').toString();
          this.loadError.set(message);
          return of<ContractAnalysisReportResponse>({
            summary: {
              total_scheduled_mwh: 0,
              total_delivered_mwh: 0,
              applications_punched: 0,
              applications_approved: 0,
              active_contracts: 0,
              approval_rate: 0,
            },
            rows: [],
          });
        }),
      )
      .subscribe((payload) => {
        this.summary.set(payload.summary);
        this.rows.set(payload.rows.map((row) => this.mapApiRow(row)));
        this.loading.set(false);
        void this.refreshComputedEstimatedProfits();
      });
  }

  private mapApiRow(row: ContractAnalysisReportResponse['rows'][number]): ContractAnalysisRow {
    const tx = row.transaction_type as ContractAnalysisTransactionType;
    const periodFromDate = this.parseDateOnly(row.period_from);
    const periodToDate = this.parseDateOnly(row.period_to);
    const periodFrom = this.formatDateDdMmYyyy(periodFromDate, row.period_from);
    const periodTo = this.formatDateDdMmYyyy(periodToDate, row.period_to);
    const periodLabel = periodFrom && periodTo ? `${periodFrom} - ${periodTo}` : periodFrom || periodTo || '-';

    const daysDifference = Number(row.days_difference || this.dayCount(periodFromDate, periodToDate));
    const timeDifference = Number(row.time_difference_hours || this.timeDifference());
    const totalQuantumMw = Number(row.requested_quantum_mw || 0);
    const approvedQuantumMw = Number(row.approved_quantum_mw || 0);
    const computedTotalQuantumMwh = this.computeTotalQuantumMwh(totalQuantumMw, daysDifference, timeDifference);
    const totalQuantumMwh = Number(row.total_quantum_mwh ?? computedTotalQuantumMwh ?? 0);
    const deliveredMwh = Number(row.delivered_mwh ?? 0);
    const apiDeviationPercent = Number(row.deviation_percent);
    const computedDeviationPercent = totalQuantumMwh ? ((totalQuantumMwh - deliveredMwh) / totalQuantumMwh) * 100 : 0;
    const deviationPercent = Number.isFinite(apiDeviationPercent) ? apiDeviationPercent : computedDeviationPercent;
    const contractDbId = (row.contract_id || '').toString();
    const contractNumber = (row.contract_number || '').toString();
    const contractId = (contractNumber || contractDbId || '').toString();

    return {
      contractDbId,
      contractNumber,
      contractId,
      buyerLoa: (row.buyer_loa_number || '-').toString(),
      sellerLoa: (row.seller_loa_number || '-').toString(),
      transactionType: tx === 'banking' || tx === 'hybrid' || tx === 'sale_purchase' ? tx : 'sale_purchase',
      buyerName: (row.buyer_name || '-').toString(),
      sellerName: (row.seller_name || '-').toString(),
      totalQuantumMw,
      totalQuantumMwh,
      approvedQuantumMw,
      deliveredMwh,
      deviationPercent: Number.isFinite(deviationPercent) ? Number(deviationPercent.toFixed(2)) : 0,
      estimatedProfit: row.estimated_profit === null || row.estimated_profit === undefined ? null : Number(row.estimated_profit),
      actualProfit: row.actual_profit === null || row.actual_profit === undefined ? null : Number(row.actual_profit),
      daysDifference,
      timeDifference,
      periodFrom: periodFromDate,
      periodTo: periodToDate,
      periodLabel,
    };
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    const raw = (value || '').trim();
    if (!raw) return null;
    const parsed = new Date(raw);
    if (!Number.isFinite(parsed.getTime())) return null;
    return parsed;
  }

  private formatDateDdMmYyyy(value: Date | null, fallbackRaw: string | null | undefined): string {
    if (!value) return ((fallbackRaw || '').trim() || '');
    const dd = String(value.getDate()).padStart(2, '0');
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const yyyy = String(value.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
  }

  private computeTotalQuantumMwh(totalQuantumMw: number, daysDifference: number, timeDifference: number): number {
    if (!Number.isFinite(totalQuantumMw) || totalQuantumMw <= 0) return 0;
    if (!Number.isFinite(daysDifference) || daysDifference <= 0) return 0;
    if (!Number.isFinite(timeDifference) || timeDifference <= 0) return 0;
    return Number((totalQuantumMw * daysDifference * timeDifference * 250).toFixed(2));
  }

  private dayCount(fromDate: Date | null, toDate: Date | null): number {
    if (!fromDate || !toDate) return 0;
    const start = Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const end = Date.UTC(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 0;
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((end - start) / msPerDay) + 1;
  }

  private timeDifference(): number {
    return 24;
  }

  protected readonly transactionTypeLabel = (value: Exclude<TransactionTypeFilter, 'all'>) => {
    switch (value) {
      case 'sale_purchase':
        return 'Sale / Purchase';
      case 'banking':
        return 'Banking';
      case 'hybrid':
        return 'Hybrid';
    }
  };

  protected updateTransactionType(value: string): void {
    const next =
      value === 'sale_purchase' || value === 'banking' || value === 'hybrid' || value === 'all' ? (value as TransactionTypeFilter) : 'all';
    this.transactionType.set(next);
  }

  protected exportReport(): void {
    const rows = this.filteredRows();
    const header = [
      'S.No',
      'Contract ID',
      'Buyer LOA',
      'Seller LOA',
      'Type',
      'Buyer',
      'Seller',
      'Total Quantum (MW)',
      'Total Quantum (MWh)',
      'Delivered (MWh)',
      'Deviation(%)',
      'Estimated Profit',
      'Actual Profit',
      'Period',
    ];
	    const data = rows.map((row, idx) => [
	      idx + 1,
	      row.contractId,
	      row.buyerLoa,
	      row.sellerLoa,
	      this.transactionTypeLabel(row.transactionType),
	      row.buyerName,
	      row.sellerName,
	      row.totalQuantumMw,
	      row.totalQuantumMwh,
	      row.deliveredMwh,
	      row.deviationPercent,
	      this.estimatedProfitForRow(row) ?? '',
	      this.actualProfitForRow(row) ?? '',
	      row.periodLabel,
	    ]);
    const csv = [header.join(','), ...data.map((line) => line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))].join(
      '\n',
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `contracts_analysis_export_${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  protected openEstimatedProfitModal(row: ContractAnalysisRow): void {
    void this.openEstimatedProfitModalLikeContractList(row);
  }

  protected estimatedProfitForRow(row: ContractAnalysisRow): number | null {
    const key = row.contractDbId || row.contractId;
    const overrides = this.computedEstimatedProfits();
    if (key && Object.prototype.hasOwnProperty.call(overrides, key)) {
      return overrides[key] ?? null;
    }
    if (row.estimatedProfit !== null && row.estimatedProfit !== undefined) return row.estimatedProfit;
    return null;
  }

  protected actualProfitForRow(row: ContractAnalysisRow): number | null {
    const key = row.contractDbId || row.contractId;
    const overrides = this.computedActualProfits();
    if (key && Object.prototype.hasOwnProperty.call(overrides, key)) {
      return overrides[key] ?? null;
    }
    return this.actualProfitBreakdownForRow(row)?.netActualProfit ?? null;
  }

  private grossActualProfitForRow(row: ContractAnalysisRow): number | null {
    if (row.actualProfit !== null && row.actualProfit !== undefined) return row.actualProfit;

    const estimatedProfit =
      row.estimatedProfit !== null && row.estimatedProfit !== undefined ? row.estimatedProfit : this.estimatedProfitForRow(row);
    const totalQuantumMwh = Number(row.totalQuantumMwh || 0);
    const deliveredMwh = Number(row.deliveredMwh || 0);
    if (estimatedProfit === null || estimatedProfit === undefined) return null;
    if (!totalQuantumMwh || !deliveredMwh) return null;

    const spread = estimatedProfit / totalQuantumMwh;
    return Number.isFinite(spread) ? Number((spread * deliveredMwh).toFixed(2)) : null;
  }

  private async refreshComputedEstimatedProfits(): Promise<void> {
    try {
      await Promise.all([this.ensureContractsLoaded(), this.ensureLoasLoaded(), this.ensureApplicationsLoaded(), this.ensureChargesLoaded()]);
    } catch {
      return;
    }

    const rows = this.rows();
    if (!rows.length) return;

    const contracts = new Map<string, Contract>();
    for (const row of rows) {
      const contract = this.resolveContractForRow(row);
      if (!contract) continue;
      contracts.set(String(contract.id), contract);
    }

    if (!contracts.size) return;

    const appsByContractId = new Map<string, ApplicationApi[]>();
    for (const app of this.rawApplications()) {
      const contractId = String(app.contract_id || '').trim();
      if (!contractId || !contracts.has(contractId)) continue;
      const list = appsByContractId.get(contractId) || [];
      list.push(app);
      appsByContractId.set(contractId, list);
    }

    const loaIds = new Set<string>();
    for (const contract of contracts.values()) {
      for (const party of contract.party_details || []) {
        if (party.loa_id) loaIds.add(party.loa_id);
      }
      for (const application of appsByContractId.get(String(contract.id)) || []) {
        const buyerLoaId = String(application.buyer_loa_id || '').trim();
        const sellerLoaId = String(application.seller_loa_id || '').trim();
        if (buyerLoaId) loaIds.add(buyerLoaId);
        if (sellerLoaId) loaIds.add(sellerLoaId);
      }
    }

    await Promise.all([...loaIds].flatMap((loaId) => [this.ensureBillConfigsLoaded(loaId), this.ensureLoaSchedulesLoaded(loaId)]));

    const grossActualProfitByContractId = new Map<string, number>();
    for (const row of rows) {
      const contractId = String(row.contractDbId || row.contractId || '').trim();
      if (!contractId) continue;
      const gross = this.grossActualProfitForRow(row);
      if (gross === null || gross === undefined) continue;
      grossActualProfitByContractId.set(contractId, gross);
    }

    const next: Record<string, number | null> = { ...this.computedEstimatedProfits() };
    const nextActual: Record<string, number | null> = { ...this.computedActualProfits() };
    for (const contract of contracts.values()) {
      const summary = this.buildEstimatedProfitSummary(contract);
      next[String(contract.id)] = summary.netProfit;

      const grossActualProfit = grossActualProfitByContractId.get(String(contract.id).trim());
      if (grossActualProfit === null || grossActualProfit === undefined) {
        nextActual[String(contract.id)] = null;
        continue;
      }
      if (grossActualProfit === 0) {
        nextActual[String(contract.id)] = 0;
        continue;
      }

      const sourceRow = rows.find((item) => String(item.contractDbId || item.contractId || '').trim() === String(contract.id).trim());
      if (!sourceRow) {
        nextActual[String(contract.id)] = null;
        continue;
      }
      nextActual[String(contract.id)] = this.actualProfitBreakdownForRow(sourceRow)?.netActualProfit ?? null;
    }
    this.computedEstimatedProfits.set(next);
    this.computedActualProfits.set(nextActual);
  }

  private async openEstimatedProfitModalLikeContractList(row: ContractAnalysisRow): Promise<void> {
    await Promise.all([this.ensureContractsLoaded(), this.ensureLoasLoaded(), this.ensureApplicationsLoaded(), this.ensureChargesLoaded()]);

    const contract = this.resolveContractForRow(row);
    if (!contract) {
      this.estimatedProfitModal.set({
        title: 'Estimated Profit',
        subtitle: row.contractId || 'Contract',
        equation: this.formatProfitValue(row.estimatedProfit),
        hint: 'Contract details were not available for this contract, so only the summary value is shown.',
        summaryLine: `Net Estimated Profit ${this.formatProfitValue(row.estimatedProfit)}`,
        details: [
          { label: 'Contract', value: row.contractId || '-' },
          { label: 'Estimated Profit', value: this.formatProfitValue(row.estimatedProfit) },
          { label: 'Actual Profit', value: this.formatProfitValue(row.actualProfit) },
        ],
      });
      return;
    }

    const loaIds = new Set<string>();
    for (const party of contract.party_details || []) {
      if (party.loa_id) loaIds.add(party.loa_id);
    }

    for (const application of this.rawApplications()) {
      if (String(application.contract_id || '').trim() !== String(contract.id)) continue;
      const buyerLoaId = String(application.buyer_loa_id || '').trim();
      const sellerLoaId = String(application.seller_loa_id || '').trim();
      if (buyerLoaId) loaIds.add(buyerLoaId);
      if (sellerLoaId) loaIds.add(sellerLoaId);
    }

    await Promise.all(
      [...loaIds].flatMap((loaId) => [this.ensureBillConfigsLoaded(loaId), this.ensureLoaSchedulesLoaded(loaId)]),
    );

    const summary = this.buildEstimatedProfitSummary(contract);
    const details: Array<{ label: string; value: string }> = [];

    for (const chargeRow of summary.chargeRows) {
      const chargeLabel = `${chargeRow.chargeType}${chargeRow.rateType ? ' (' + chargeRow.rateType + ')' : ''}`;
      details.push({
        label: `${chargeLabel}`,
        value: `${this.formatProfitValue(chargeRow.totalAmount)} | Buyer ${this.formatProfitValue(chargeRow.buyerAmount)} | Seller ${this.formatProfitValue(chargeRow.sellerAmount)} | SECI ${this.formatProfitValue(chargeRow.seciAmount)} | ${chargeRow.allocationLabel}`,
      });
    }

    this.estimatedProfitModal.set({
      title: 'Estimated Profit',
      subtitle: contract.contract_number || 'Contract',
      equation:
        `${this.formatProfitValue(summary.netProfit)} = ` +
        `Buyer Cost ${this.formatProfitValue(summary.revenue)} - ` +
        `Seller Cost ${this.formatProfitValue(summary.cost)} - ` +
        `Application Charges ${this.formatProfitValue(summary.applicationCharges)}`,
      hint: summary.chargeRows.length
        ? '11Application charges are split to buyer and seller when mapped in OA charge allocation. Any unmapped balance is counted as SECI and reduced from Estimated Profit.'
        : 'No application charge bifurcation was found for this contract.',
      summaryLine:
        `Buyer Cost ${this.formatProfitValue(summary.revenue)} | ` +
        `Seller Cost ${this.formatProfitValue(summary.cost)} | ` +
        `Application Charges ${this.formatProfitValue(summary.applicationCharges)} | ` +
        `Net Estimated Profit ${this.formatProfitValue(summary.netProfit)}`,
      details,
    });
  }

  protected openActualProfitModal(row: ContractAnalysisRow): void {
    void this.openActualProfitModalLikeContractList(row);
  }

  private async openActualProfitModalLikeContractList(row: ContractAnalysisRow): Promise<void> {
    await Promise.all([this.ensureContractsLoaded(), this.ensureLoasLoaded(), this.ensureApplicationsLoaded(), this.ensureChargesLoaded()]);
    const breakdown = this.actualProfitBreakdownForRow(row);
    if (!breakdown) return;
    const { grossActualProfit, chargeRows, chargeTotal, netActualProfit } = breakdown;

    const contract = this.resolveContractForRow(row);
    const contractId = contract ? String(contract.id) : String(row.contractDbId || '').trim();

    const loaIds = new Set<string>();
    if (contract) {
      for (const party of contract.party_details || []) {
        if (party.loa_id) loaIds.add(party.loa_id);
      }
    }

    for (const application of this.rawApplications()) {
      if (String(application.contract_id || '').trim() !== contractId) continue;
      const buyerLoaId = String(application.buyer_loa_id || '').trim();
      const sellerLoaId = String(application.seller_loa_id || '').trim();
      if (buyerLoaId) loaIds.add(buyerLoaId);
      if (sellerLoaId) loaIds.add(sellerLoaId);
    }

    await Promise.all([...loaIds].map((loaId) => this.ensureBillConfigsLoaded(loaId)));
    const chargeLabel = 'Application Charges';
    const chargeHint = chargeRows.length
      ? 'Application charges shown here use approved quantum for MWh-based charges and follow the same charge mapping used in Estimated Profit.'
      : 'No application charge bifurcation was found for this contract.';
    const details: Array<{ label: string; value: string }> = [];

    for (const chargeRow of chargeRows) {
      const detailLabel = `${chargeRow.chargeType}${chargeRow.rateType ? ' (' + chargeRow.rateType + ')' : ''}`;
      details.push({
        label: `${detailLabel}`,
        value: `${this.formatProfitValue(chargeRow.totalAmount)} | Buyer ${this.formatProfitValue(chargeRow.buyerAmount)} | Seller ${this.formatProfitValue(chargeRow.sellerAmount)} | SECI ${this.formatProfitValue(chargeRow.seciAmount)} | ${chargeRow.allocationLabel}`,
      });
    }

	    this.estimatedProfitModal.set({
	      title: 'Actual Profit',
	      subtitle: contract?.contract_number || row.contractId || 'Contract',
	      equation:
	        `${this.formatProfitValue(netActualProfit)} = ` +
	        `Gross Actual Profit ${this.formatProfitValue(grossActualProfit)} - ` +
	        `${chargeLabel} ${this.formatProfitValue(chargeTotal)}`,
	      hint: chargeHint,
	      summaryLine:
	        `Gross Actual Profit ${this.formatProfitValue(grossActualProfit)} | ` +
	        `${chargeLabel} ${this.formatProfitValue(chargeTotal)} | ` +
	        `Net Actual Profit ${this.formatProfitValue(netActualProfit)}`,
	      details,
	    });
	  }

  private actualProfitBreakdownForRow(row: ContractAnalysisRow): {
    grossActualProfit: number;
    chargeRows: EstimatedProfitChargeRow[];
    chargeTotal: number;
    netActualProfit: number;
  } | null {
    const grossActualProfit = this.grossActualProfitForRow(row);
    if (grossActualProfit === null || grossActualProfit === undefined) return null;
    if (grossActualProfit === 0) {
      return { grossActualProfit: 0, chargeRows: [], chargeTotal: 0, netActualProfit: 0 };
    }

    const contract = this.resolveContractForRow(row);
    const chargeRows = contract
      ? this.resolveActualProfitChargeRows(contract, row)
      : row.contractDbId || row.contractId
        ? this.resolvePunchedProfitChargeRowsForContractId(String(row.contractDbId || row.contractId))
        : [];
    const chargeTotal = Number(chargeRows.reduce((sum, item) => sum + item.totalAmount, 0).toFixed(2));
    const netActualProfit = Number((grossActualProfit - chargeTotal).toFixed(2));
    return { grossActualProfit, chargeRows, chargeTotal, netActualProfit };
  }

  private resolveActualProfitChargeRows(contract: Contract, row: ContractAnalysisRow): EstimatedProfitChargeRow[] {
    const baseRows = this.resolveEstimatedProfitChargeRows(contract).map((item) => ({ ...item }));
    const requestedMw = Number(row.totalQuantumMw || 0);
    const approvedMw = Number(row.approvedQuantumMw || 0);
    const ratio = Number.isFinite(requestedMw) && requestedMw > 0 && Number.isFinite(approvedMw) && approvedMw >= 0 ? approvedMw / requestedMw : 0;
    const safeRatio = Math.max(ratio, 0);

    if (approvedMw <= 0) {
      return baseRows.map((item) => ({
        ...item,
        totalAmount: 0,
        buyerAmount: 0,
        sellerAmount: 0,
        seciAmount: 0,
        allocationLabel: `${item.allocationLabel}${item.allocationLabel ? ' • ' : ''}Approved quantum is 0, so actual charge is 0`,
      }));
    }

    return baseRows.map((item) => {
      if (String(item.rateType || '').trim() !== 'Rate (₹/MWh)') return item;
      return {
        ...item,
        totalAmount: Number((item.totalAmount * safeRatio).toFixed(2)),
        buyerAmount: Number((item.buyerAmount * safeRatio).toFixed(2)),
        sellerAmount: Number((item.sellerAmount * safeRatio).toFixed(2)),
        seciAmount: Number((item.seciAmount * safeRatio).toFixed(2)),
        allocationLabel: `${item.allocationLabel}${item.allocationLabel ? ' • ' : ''}Approved quantum scaling ${this.formatNumericValue(safeRatio * 100)}%`,
      };
    });
  }

  protected closeEstimatedProfitModal(): void {
    this.estimatedProfitModal.set(null);
  }

  protected formatProfitValue(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'NA';
    return `Rs ${this.formatNumericValue(value)}`;
  }

  protected profitValueClass(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'profit-value-neutral';
    if (value < 0) return 'profit-value-negative';
    return 'profit-value-positive';
  }

  private async ensureContractsLoaded(): Promise<void> {
    if (this.contracts().length || this.contractsLoading()) return;
    this.contractsLoading.set(true);
    try {
      const items = await firstValueFrom(this.contractsService.list());
      this.contracts.set(items || []);
    } catch {
      this.contracts.set([]);
    } finally {
      this.contractsLoading.set(false);
    }
  }

  private async ensureLoasLoaded(): Promise<void> {
    if (this.loas().length || this.loasLoading()) return;
    this.loasLoading.set(true);
    try {
      const items = await firstValueFrom(this.loaService.list());
      this.loas.set(items || []);
    } catch {
      this.loas.set([]);
    } finally {
      this.loasLoading.set(false);
    }
  }

  private async ensureApplicationsLoaded(): Promise<void> {
    if (this.rawApplications().length || this.applicationsLoading()) return;
    this.applicationsLoading.set(true);
    try {
      const items = await firstValueFrom(this.api.get<ApplicationApi[]>('/api/applications'));
      this.rawApplications.set(items || []);
    } catch {
      this.rawApplications.set([]);
    } finally {
      this.applicationsLoading.set(false);
    }
  }

  private async ensureChargesLoaded(): Promise<void> {
    if (this.chargeMasters().length || this.chargesLoading()) return;
    this.chargesLoading.set(true);
    try {
      const items = await firstValueFrom(this.chargesService.list());
      this.chargeMasters.set(items || []);
    } catch {
      this.chargeMasters.set([]);
    } finally {
      this.chargesLoading.set(false);
    }
  }

  private async ensureBillConfigsLoaded(loaId: string): Promise<void> {
    if (!loaId) return;
    if (this.billConfigsByLoa()[loaId]) return;
    const existing = this.billConfigsRequests.get(loaId);
    if (existing) return existing;

    const request = (async () => {
      try {
        const items = await firstValueFrom(this.loaService.listBillConfigs(loaId));
        this.billConfigsByLoa.set({
          ...this.billConfigsByLoa(),
          [loaId]: items || [],
        });
      } catch {
        this.billConfigsByLoa.set({
          ...this.billConfigsByLoa(),
          [loaId]: [],
        });
      } finally {
        this.billConfigsRequests.delete(loaId);
      }
    })();

    this.billConfigsRequests.set(loaId, request);
    return request;
  }

  private async ensureLoaSchedulesLoaded(loaId: string): Promise<void> {
    if (!loaId) return;
    if (this.loaSchedulesByLoa()[loaId]) return;
    const existing = this.loaScheduleRequests.get(loaId);
    if (existing) return existing;

    const request = (async () => {
      try {
        const items = await firstValueFrom(this.loaService.listSchedules(loaId));
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: items || [],
        });
      } catch {
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: [],
        });
      } finally {
        this.loaScheduleRequests.delete(loaId);
      }
    })();

    this.loaScheduleRequests.set(loaId, request);
    return request;
  }

  private resolveContractForRow(row: ContractAnalysisRow): Contract | null {
    const normalizedContractDbId = String(row.contractDbId || '').trim();
    const normalizedContractNumber = String(row.contractNumber || row.contractId || '').trim();
    if (row.contractDbId) {
      const matchById = this.contracts().find((item) => String(item.id || '').trim() === normalizedContractDbId);
      if (matchById) return matchById;
    }
    if (normalizedContractNumber) {
      const matchByNumber = this.contracts().find((item) => String(item.contract_number || '').trim() === normalizedContractNumber);
      if (matchByNumber) return matchByNumber;
    }
    const normalizedBuyerLoa = String(row.buyerLoa || '').trim().toUpperCase();
    const normalizedSellerLoa = String(row.sellerLoa || '').trim().toUpperCase();
    const periodFrom = row.periodFrom ? row.periodFrom.getTime() : null;
    const periodTo = row.periodTo ? row.periodTo.getTime() : null;

    for (const contract of this.contracts()) {
      const contractLoas = new Set(
        (contract.party_details || [])
          .map((party) => this.loas().find((loa) => loa.id === party.loa_id))
          .map((loa) => String(loa?.loa_number || '').trim().toUpperCase())
          .filter((value) => !!value),
      );
      if (normalizedBuyerLoa && !contractLoas.has(normalizedBuyerLoa)) continue;
      if (normalizedSellerLoa && !contractLoas.has(normalizedSellerLoa)) continue;

      const contractFrom = this.parseDateOnlyYmd(contract.period_from)?.getTime() ?? null;
      const contractTo = this.parseDateOnlyYmd(contract.period_to)?.getTime() ?? null;
      if (periodFrom !== null && contractFrom !== null && periodFrom !== contractFrom) continue;
      if (periodTo !== null && contractTo !== null && periodTo !== contractTo) continue;

      return contract;
    }
    return null;
  }

  private buildEstimatedProfitSummary(contract: Contract): EstimatedProfitSummary {
    const parties = contract.party_details || [];
    if (!parties.length) {
      return { revenue: 0, cost: 0, applicationCharges: 0, netProfit: null, chargeRows: [] };
    }

    const contractStart = this.parseDateOnlyYmd(contract.period_from);
    const contractEnd = this.parseDateOnlyYmd(contract.period_to);
    if (!contractStart || !contractEnd) {
      return { revenue: 0, cost: 0, applicationCharges: 0, netProfit: null, chargeRows: [] };
    }

    let hasKnownContribution = false;
    let revenue = 0;
    let cost = 0;

    for (const party of parties) {
      const loa = this.loas().find((item) => item.id === party.loa_id);
      const loaType = (party.party_type || this.resolveLoaTypeFromLoa(loa)).toLowerCase().trim();
      if (loaType === 'banking') continue;

      const rate = this.resolveEstimatedRate(loa, this.billConfigsByLoa()[party.loa_id] || []);
      if (rate === null) continue;

      const contractedUnits = this.resolveEstimatedContractUnits(contractStart, contractEnd, loa, party.quantum);
      if (!contractedUnits) continue;

      hasKnownContribution = true;
      if (loaType === 'buy') revenue += contractedUnits * rate;
      if (loaType === 'sell') cost += contractedUnits * rate;
    }

    const chargeRows = this.resolveEstimatedProfitChargeRows(contract);
    const applicationCharges = chargeRows.reduce((sum, chargeRow) => sum + chargeRow.totalAmount, 0);
    return {
      revenue,
      cost,
      applicationCharges,
      netProfit: hasKnownContribution ? revenue - cost - applicationCharges : null,
      chargeRows,
    };
  }

  private resolveEstimatedProfitChargeRows(contract: Contract): EstimatedProfitChargeRow[] {
    const configured = this.resolveConfiguredProfitChargeRows(contract);
    if (configured.length) return configured;
    return this.resolvePunchedProfitChargeRows(contract);
  }

  private resolvePunchedProfitChargeRows(contract: Contract): EstimatedProfitChargeRow[] {
    const rows: EstimatedProfitChargeRow[] = [];
    const masters = this.configuredChargesForContract(contract);
    const basis = this.resolveContractChargeBasis(contract);

    for (const application of this.rawApplications()) {
      if (String(application.contract_id || '').trim() !== String(contract.id).trim()) continue;
      const approvalCharges = application.approval_charges || [];
      if (!approvalCharges.length) continue;

      const allocationMap = this.oaChargeAllocationMap(application);

      for (const [index, charge] of approvalCharges.entries()) {
        const allocationKey = this.normalizeApplicationChargeType(charge, index);
        const master = this.resolveChargeMasterFromChargeMasters(masters, allocationKey);
        const rateType = (master?.rate_type || '').trim() || null;
        const masterValue = master ? Number(master.value) : Number.NaN;
        const fallbackAmount = this.resolveApplicationChargeAmount(charge);
        const displayChargeName = String(master?.charge_name || '').trim() || allocationKey;
        const regionLabel = master?.charge_name || allocationKey;
        const requiredRegion = this.extractRegionCode(regionLabel);
        if (requiredRegion && !this.shouldApplyRegionalCharge(contract, regionLabel)) continue;
        const totalAmount =
          master && Number.isFinite(masterValue) ? this.resolveChargeAmountFromRateType(masterValue, rateType, basis) : fallbackAmount;
        const allocation = allocationMap.get(allocationKey);
        const buyerPercent = this.sanitizePercent(allocation?.buyer_percent);
        const sellerPercent = this.cappedSellerPercent(allocation?.seller_percent, buyerPercent);
        const mappedPercent = buyerPercent + sellerPercent;
        const buyerAmount = (totalAmount * buyerPercent) / 100;
        const sellerAmount = (totalAmount * sellerPercent) / 100;
        const seciAmount = Math.max(totalAmount - buyerAmount - sellerAmount, 0);
        rows.push({
          applicationNumber: application.application_number || String(application.id),
          chargeType: displayChargeName,
          rateType,
          totalAmount,
          buyerAmount,
          sellerAmount,
          seciAmount,
          allocationLabel: !allocation
            ? 'No buyer/seller mapping found, so the remaining amount is counted on SECI'
            : `Buyer ${this.formatNumericValue(buyerPercent)}% / Seller ${this.formatNumericValue(sellerPercent)}% / SECI ${this.formatNumericValue(Math.max(100 - mappedPercent, 0))}%`,
        });
      }
    }

    return rows;
  }

  private resolvePunchedProfitChargeRowsForContractId(contractId: string): EstimatedProfitChargeRow[] {
    const rows: EstimatedProfitChargeRow[] = [];

    for (const application of this.rawApplications()) {
      if (String(application.contract_id || '').trim() !== String(contractId).trim()) continue;
      const approvalCharges = application.approval_charges || [];
      if (!approvalCharges.length) continue;

      const allocationMap = this.oaChargeAllocationMap(application);

      for (const [index, charge] of approvalCharges.entries()) {
        const chargeType = this.normalizeApplicationChargeType(charge, index);
        const totalAmount = this.resolveApplicationChargeAmount(charge);
        const allocation = allocationMap.get(chargeType);
        const buyerPercent = this.sanitizePercent(allocation?.buyer_percent);
        const sellerPercent = this.cappedSellerPercent(allocation?.seller_percent, buyerPercent);
        const mappedPercent = buyerPercent + sellerPercent;
        const buyerAmount = (totalAmount * buyerPercent) / 100;
        const sellerAmount = (totalAmount * sellerPercent) / 100;
        const seciAmount = Math.max(totalAmount - buyerAmount - sellerAmount, 0);
        rows.push({
          applicationNumber: application.application_number || String(application.id),
          chargeType,
          rateType: null,
          totalAmount,
          buyerAmount,
          sellerAmount,
          seciAmount,
          allocationLabel: !allocation
            ? 'No buyer/seller mapping found, so the remaining amount is counted on SECI'
            : `Buyer ${this.formatNumericValue(buyerPercent)}% / Seller ${this.formatNumericValue(sellerPercent)}% / SECI ${this.formatNumericValue(Math.max(100 - mappedPercent, 0))}%`,
        });
      }
    }

    return rows;
  }

  private resolveConfiguredProfitChargeRows(contract: Contract): EstimatedProfitChargeRow[] {
    const charges = this.configuredChargesForContract(contract);
    if (!charges.length) return [];

    const loaIds = (contract.party_details || [])
      .map((party) => String(party.loa_id || '').trim())
      .filter((id) => !!id);
    const allocationMap = this.oaChargeAllocationMapFromLoaIds(loaIds);

    const rows: EstimatedProfitChargeRow[] = [];
    for (const charge of charges) {
      const requiredRegion = this.extractRegionCode(charge.charge_name);
      if (requiredRegion && !this.shouldApplyRegionalCharge(contract, charge.charge_name)) continue;

      const amount = this.resolveConfiguredChargeAmount(contract, charge);
      if (!Number.isFinite(amount) || amount <= 0) continue;

      const allocationKey = this.normalizeChargeAllocationType(`${charge.charge_name || ''} ${charge.charge_type || ''}`.trim());
      const allocation = allocationMap.get(allocationKey);
      const buyerPercent = this.sanitizePercent(allocation?.buyer_percent);
      const sellerPercent = this.cappedSellerPercent(allocation?.seller_percent, buyerPercent);
      const mappedPercent = buyerPercent + sellerPercent;
      const buyerAmount = (amount * buyerPercent) / 100;
      const sellerAmount = (amount * sellerPercent) / 100;
      const seciAmount = Math.max(amount - buyerAmount - sellerAmount, 0);

      rows.push({
        applicationNumber: String(charge.charge_type || '').trim() || 'Charge Master',
        chargeType: String(charge.charge_name || '').trim() || allocationKey || 'Charge',
        rateType: (charge.rate_type || '').trim() || null,
        totalAmount: Number(amount.toFixed(2)),
        buyerAmount: Number(buyerAmount.toFixed(2)),
        sellerAmount: Number(sellerAmount.toFixed(2)),
        seciAmount: Number(seciAmount.toFixed(2)),
        allocationLabel: !allocation
          ? `Effective ${charge.effective_date} • No buyer/seller mapping found, so the remaining amount is counted on SECI`
          : `Effective ${charge.effective_date} • Buyer ${this.formatNumericValue(buyerPercent)}% / Seller ${this.formatNumericValue(sellerPercent)}% / SECI ${this.formatNumericValue(Math.max(100 - mappedPercent, 0))}%`,
      });
    }
    return rows;
  }

  private extractRegionCode(value: string | null | undefined): string | null {
    const text = String(value || '').trim().toUpperCase();
    if (!text) return null;

    const explicit = /\b(ERLDC|NERLDC|NRLDC|SRLDC|WRLDC)\b/.exec(text);
    if (explicit) return explicit[1];

    if (/\bNORTH\s*EAST\b/.test(text) || /\bNORTHEAST\b/.test(text)) return 'NERLDC';
    if (/\bEAST\b/.test(text) || /\bEASTERN\b/.test(text)) return 'ERLDC';
    if (/\bNORTH\b/.test(text) || /\bNORTHERN\b/.test(text)) return 'NRLDC';
    if (/\bSOUTH\b/.test(text) || /\bSOUTHERN\b/.test(text)) return 'SRLDC';
    if (/\bWEST\b/.test(text) || /\bWESTERN\b/.test(text)) return 'WRLDC';

    return null;
  }

  private contractRegionCodes(contract: Contract): Set<string> {
    const set = new Set<string>();
    for (const party of contract.party_details || []) {
      const code = this.extractRegionCode(party.rldc);
      if (code) set.add(code);
    }
    return set;
  }

  private shouldApplyRegionalCharge(contract: Contract, chargeLabel: string | null | undefined): boolean {
    const required = this.extractRegionCode(chargeLabel);
    if (!required) return true;
    return this.contractRegionCodes(contract).has(required);
  }

  private resolveChargeMasterFromChargeMasters(masters: Charge[], chargeLabel: string): Charge | null {
    const needle = String(chargeLabel || '').trim().toUpperCase();
    if (!needle) return null;

    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const containsNeedle = new RegExp(`\\b${escaped}\\b`, 'i');

    let best: Charge | null = null;
    for (const row of masters || []) {
      const group = String(row?.charge_type || '').trim();
      const name = String(row?.charge_name || '').trim();
      if (!group && !name) continue;

      const matchesGroup = !!group && (group.trim().toUpperCase() === needle || containsNeedle.test(group));
      const matchesName = !!name && (name.trim().toUpperCase() === needle || containsNeedle.test(name));
      if (!matchesGroup && !matchesName) continue;

      if (!best || String(row.effective_date || '') > String(best.effective_date || '')) best = row;
    }

    return best;
  }

  private resolveContractChargeBasis(contract: Contract): { days: number; energyMwh: number } {
    const contractStart = this.parseDateOnlyYmd(contract.period_from);
    const contractEnd = this.parseDateOnlyYmd(contract.period_to);
    if (!contractStart || !contractEnd) return { days: 0, energyMwh: 0 };

    const daysDifference = this.inclusiveDayCount(contractStart, contractEnd);

    const energyByLoa = new Map<string, number>();
    for (const party of contract.party_details || []) {
      const loaId = String(party.loa_id || '').trim();
      if (!loaId || energyByLoa.has(loaId)) continue;

      const schedules = this.loaSchedulesByLoa()[loaId] || [];
      const scheduleEnergy = schedules.reduce((total, schedule) => {
        const scheduleStart = this.parseDateOnlyYmd(schedule.from_date);
        const scheduleEnd = this.parseDateOnlyYmd(schedule.to_date);
        if (!scheduleStart || !scheduleEnd) return total;

        const overlapDays = this.overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
        if (!overlapDays) return total;

        const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
        if (!timeDifference) return total;

        const quantum = this.firstFiniteNumber(schedule.quantum_mw, party.quantum);
        if (quantum === null) return total;

        return total + overlapDays * timeDifference * quantum;
      }, 0);

      if (scheduleEnergy > 0) {
        energyByLoa.set(loaId, scheduleEnergy);
        continue;
      }

      const quantum = this.firstFiniteNumber(party.quantum);
      energyByLoa.set(loaId, quantum === null ? 0 : daysDifference * 24 * quantum);
    }

    const energyMwh = energyByLoa.size > 0 ? Math.max(...energyByLoa.values()) : 0;
    return { days: daysDifference, energyMwh };
  }

  private resolveChargeAmountFromRateType(value: number, rateType: string | null | undefined, basis: { days: number; energyMwh: number }): number {
    if (!Number.isFinite(value) || value < 0) return 0;
    const normalized = String(rateType || '').trim();
    if (normalized === 'Rate (₹/MWh)') return value * basis.energyMwh;
    if (normalized === 'Rate (₹/Day)') return value * basis.days;
    return value;
  }

  private configuredChargeCutoff(contract: Contract): string {
    return (
      this.normalizeDate(contract.period_from) ||
      this.normalizeDate((contract as Partial<Contract>).issue_date) ||
      this.normalizeDate(contract.created_at) ||
      ''
    );
  }

  private configuredChargesForContract(contract: Contract): Charge[] {
    const cutoff = this.configuredChargeCutoff(contract);
    if (!cutoff) return [];

    const activeCharges = (this.chargeMasters() || [])
      .filter((row) => !!row && !!row.active)
      .filter((row) => String(row.effective_date || '').trim() !== '' && String(row.effective_date).trim() <= cutoff);

    const byKey = new Map<string, Charge>();
    for (const row of activeCharges) {
      const group = String(row.charge_type || '').trim().toUpperCase();
      const name = String(row.charge_name || '').trim().toUpperCase();
      const key = `${group}|${name}`;
      const existing = byKey.get(key);
      if (!existing || String(row.effective_date || '') > String(existing.effective_date || '')) {
        byKey.set(key, row);
      }
    }

    return [...byKey.values()].sort((left, right) => {
      const groupCompare = String(left.charge_type || '').localeCompare(String(right.charge_type || ''));
      if (groupCompare !== 0) return groupCompare;
      const nameCompare = String(left.charge_name || '').localeCompare(String(right.charge_name || ''));
      if (nameCompare !== 0) return nameCompare;
      return String(right.effective_date || '').localeCompare(String(left.effective_date || ''));
    });
  }

  private resolveConfiguredChargeAmount(contract: Contract, charge: Charge): number {
    const value = Number(charge.value);
    if (!Number.isFinite(value) || value < 0) return 0;
    const basis = this.resolveContractChargeBasis(contract);
    const computed = this.resolveChargeAmountFromRateType(value, charge.rate_type, basis);
    return Number((Number.isFinite(computed) ? computed : 0).toFixed(2));
  }

  private parseOaChargeAllocation(raw: string | null | undefined): OaChargeAllocation[] {
    if (!raw || !raw.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const charge_type = this.normalizeChargeAllocationType(String((item as Partial<OaChargeAllocation>).charge_type || '').trim());
          if (!charge_type) return null;
          const payerInput = String((item as Partial<OaChargeAllocation>).payer || 'Shared');
          const payer: OaChargeAllocation['payer'] =
            payerInput === 'Buyer' || payerInput === 'Seller' || payerInput === 'Shared' ? payerInput : 'Shared';
          const buyer = Number((item as Partial<OaChargeAllocation>).buyer_percent);
          const seller = Number((item as Partial<OaChargeAllocation>).seller_percent);
          return {
            charge_type,
            payer,
            buyer_percent: Number.isFinite(buyer) ? buyer : 0,
            seller_percent: Number.isFinite(seller) ? seller : 0,
          };
        })
        .filter((item): item is OaChargeAllocation => item !== null);
    } catch {
      return [];
    }
  }

  private latestOaChargeAllocation(loaId: string): string | null {
    if (!loaId) return null;
    const configs = this.billConfigsByLoa()[loaId] || [];
    const latest = [...configs]
      .filter((item) => (item.bill_type || '').trim().toUpperCase() === 'OA')
      .sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''))[0];
    return latest?.oa_charge_allocation || null;
  }

  private oaChargeAllocationMap(application: ApplicationApi): Map<string, OaChargeAllocation> {
    const loaIds = [String(application.seller_loa_id || '').trim(), String(application.buyer_loa_id || '').trim()].filter((id) => !!id);
    const map = new Map<string, OaChargeAllocation>();
    for (const loaId of loaIds) {
      const items = this.parseOaChargeAllocation(this.latestOaChargeAllocation(loaId));
      for (const item of items) {
        if (!map.has(item.charge_type)) {
          map.set(item.charge_type, item);
        }
      }
    }
    return map;
  }

  private oaChargeAllocationMapFromLoaIds(loaIds: string[]): Map<string, OaChargeAllocation> {
    const uniqueLoaIds = (loaIds || []).map((id) => String(id || '').trim()).filter((id) => !!id);
    const map = new Map<string, OaChargeAllocation>();
    for (const loaId of uniqueLoaIds) {
      const items = this.parseOaChargeAllocation(this.latestOaChargeAllocation(loaId));
      for (const item of items) {
        if (!map.has(item.charge_type)) {
          map.set(item.charge_type, item);
        }
      }
    }
    return map;
  }

  private normalizeChargeAllocationType(value: string): string {
    const normalized = value.trim().toUpperCase();
    if (!normalized) return '';
    if (normalized.includes('TRANSMISSION')) return 'Transmission';
    if (normalized.includes('WHEEL')) return 'Wheeling';
    if (normalized.includes('SLDC')) return 'SLDC';
    if (normalized.includes('RLDC')) return 'RLDC';
    if (normalized.includes('LOSS')) return 'Loss';
    if (normalized.includes('APP')) return 'App';
    return value.trim();
  }

  private normalizeApplicationChargeType(item: ApplicationChargeApi, index: number): string {
    const raw = `${item.sub_group_name || ''} ${item.group_name || ''} ${item.section_heading || ''}`.trim();
    if (!raw) return `Charge ${index + 1}`;
    return this.normalizeChargeAllocationType(raw);
  }

  private resolveApplicationChargeAmount(item: ApplicationChargeApi): number {
    const amount = Number(item.amount);
    if (Number.isFinite(amount) && amount !== 0) return amount;
    return this.asNumber(item.rate_mwh) * this.asNumber(item.quantum_mwh);
  }

  private sanitizePercent(value: number | null | undefined): number {
    if (!Number.isFinite(Number(value))) return 0;
    return Math.min(Math.max(Number(value), 0), 100);
  }

  private cappedSellerPercent(value: number | null | undefined, buyerPercent: number): number {
    return Math.min(this.sanitizePercent(value), Math.max(100 - buyerPercent, 0));
  }

  private normalizeDate(value: string | null | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.slice(0, 10);
    return date.toISOString().slice(0, 10);
  }

  private parseDateOnlyYmd(value: string | null | undefined): Date | null {
    if (!value) return null;
    const normalized = this.normalizeDate(value);
    if (!normalized) return null;
    const [year, month, day] = normalized.split('-').map((part) => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private resolveEstimatedRate(loa: Loa | null | undefined, billConfigs: LoaBillConfig[]): number | null {
    const datedConfigs = [...billConfigs].sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''));
    const preferredConfigs = datedConfigs.filter((item) => (item.bill_type || '').trim().toUpperCase() !== 'OA');

    for (const config of preferredConfigs.length ? preferredConfigs : datedConfigs) {
      const directRate = this.firstFiniteNumber(config.rate, config.tmr_rate, config.mcp_rate);
      if (directRate !== null) return directRate;
    }

    return this.firstFiniteNumber(loa?.fixed_rate, loa?.mcp_value);
  }

  private resolveLoaTypeFromLoa(loa: Loa | null | undefined): string {
    const value = (loa?.category || '').trim().toLowerCase();
    if (!value) return '';
    if (value.includes('bank')) return 'Banking';
    if (value.includes('buy') || value.includes('purchase')) return 'Buy';
    if (value.includes('sell') || value.includes('sale')) return 'Sell';
    return loa?.category || '';
  }

  private resolveEstimatedContractUnits(
    contractStart: Date,
    contractEnd: Date,
    loa: Loa | null | undefined,
    fallbackQuantum: string | number | null | undefined,
  ): number {
    const schedules = loa?.id ? this.loaSchedulesByLoa()[loa.id] || [] : [];
    const scheduleUnits = schedules.reduce((total, schedule) => {
      const scheduleStart = this.parseDateOnlyYmd(schedule.from_date);
      const scheduleEnd = this.parseDateOnlyYmd(schedule.to_date);
      if (!scheduleStart || !scheduleEnd) return total;

      const overlapDays = this.overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
      if (!overlapDays) return total;

      const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!timeDifference) return total;

      const quantum = this.firstFiniteNumber(schedule.quantum_mw, fallbackQuantum);
      if (quantum === null) return total;

      return total + overlapDays * timeDifference * quantum * 250;
    }, 0);

    if (scheduleUnits > 0) return scheduleUnits;

    const daysDifference = this.inclusiveDayCount(contractStart, contractEnd);
    const quantum = this.firstFiniteNumber(fallbackQuantum);
    if (!daysDifference || quantum === null) return 0;

    return daysDifference * 24 * quantum * 250;
  }

  private inclusiveDayCount(start: Date, end: Date): number {
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  }

  private overlapDayCount(rangeStart: Date, rangeEnd: Date, scheduleStart: Date, scheduleEnd: Date): number {
    const start = Math.max(rangeStart.getTime(), scheduleStart.getTime());
    const end = Math.min(rangeEnd.getTime(), scheduleEnd.getTime());
    if (end < start) return 0;
    return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
  }

  private timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const fromMinutes = this.parseTimeToMinutes(fromTime);
    const toMinutes = this.parseTimeToMinutes(toTime);
    if (fromMinutes === null || toMinutes === null) return 0;
    if (toMinutes >= fromMinutes) return (toMinutes - fromMinutes) / 60;
    return (24 * 60 - fromMinutes + toMinutes) / 60;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value) return null;
    const raw = value.trim();
    if (!raw) return null;
    const [hourText, minuteText] = raw.split(':');
    const hours = Number(hourText);
    const minutes = Number(minuteText);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null;
    if (hours === 24) return 24 * 60;
    return hours * 60 + minutes;
  }

  private asNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private firstFiniteNumber(...values: Array<string | number | null | undefined>): number | null {
    for (const value of values) {
      const parsed = this.asNumber(value);
      if (value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) && parsed !== 0) {
        return parsed;
      }
    }
    return null;
  }

  private formatNumericValue(value: number): string {
    return Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0';
  }
}
